import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { UserName } from "../../../context/profileUI";
import {
    LikeBar,
    Comments,
    QA,
    ReactionBar,
    Bookmark,
    Spoiler,
    PresenceBadge,
    Discussion,
    MissionCompleted,
} from "../components";
export default function MissionDetailContent({
    m,
    uid,
    coverUrl,        // ✅ ADD THIS
    galleryUrls,
    visibilityLabel,
    featuredReaction,
    selectedReactionFromBar,

    completion,
    yt,
    onShare,
    linkCopied,
    canEdit,
    setEditOpen,
    openLightbox,

    hasObjectives,
    primaryChecklist,
    optionalChecklist,

    primaryCount,
    optionalCount,
    donePrimary,
    doneOptional,

    doneAll,
    totalObjectives,
    pct,

    /* NEW */
    presenceKey,
    missionId,
    basePath,
    bookmarkKey,
    onReactionSelect,
}) {
    if (!m) return null;

    return (
        <>
            {/* HEADER */}
            <header className="msn-detail-header">
                <div className="msn-chips">
                    {m.game && (
                        <span className="msn-chip msn-chip--game">
                            🎮 {m.game}
                        </span>
                    )}

                    <span className="msn-chip">{m.category || "Mission"}</span>

                    {m.missionNumber != null && (
                        <span className="msn-chip"># {m.missionNumber}</span>
                    )}

                    {m.difficulty && <span className="msn-chip">🛡 {m.difficulty}</span>}
                    {m.estDurationMins && (
                        <span className="msn-chip">⏱ ~{m.estDurationMins}m</span>
                    )}
                </div>


                <h1 className="msn-detail-title">
                    {m.missionName}
                    {m.createdBy === uid && (
                        <>
                            <span title="Your mission" aria-label="Your mission">
                                {" "}
                                👑
                            </span>
                            {visibilityLabel && (
                                <span
                                    className={`msn-chip ${visibilityLabel === "Public"
                                        ? "msn-chip--public"
                                        : "msn-chip--private"
                                        }`}
                                    title={`Visibility: ${visibilityLabel}`}
                                    aria-label={`Visibility: ${visibilityLabel}`}
                                    style={{ marginLeft: "0.5rem" }}
                                >
                                    {visibilityLabel === "Public" ? "🌐 Public" : "🔒 Private"}
                                </span>
                            )}
                        </>
                    )}
                </h1>

                {/* ✅ put the featured/selected reaction right here */}
                {(featuredReaction || selectedReactionFromBar) && (
                    <div className="msn-featured-reaction" aria-hidden>
                        {(() => {
                            const id = selectedReactionFromBar || featuredReaction;
                            const map = {
                                like: "👍 Like",
                                love: "❤️ Love",
                                fire: "🔥 Hot",
                                helpful: "💡 Helpful",
                                strategy: "🎯 Strategy",
                                insightful: "🧠 Insightful",
                            };
                            return map[id] || id;
                        })()}
                    </div>
                )}

                {/* creator */}
                {m.createdBy && (
                    <div className="msn-meta">
                        <span>
                            👤 Created by{" "}
                            <strong>
                                <UserName userId={m.createdBy} fallback="Player" />
                            </strong>
                        </span>
                    </div>
                )}

                <div className="msn-meta">
                    {m.location && <span>📍 {m.location}</span>}
                    {Array.isArray(m.tags) && m.tags.length > 0 && (
                        <span className="msn-tags">
                            {m.tags.map((t) => (
                                <Link
                                    key={t}
                                    to={`/missions?tag=${encodeURIComponent(t)}`}
                                    className="msn-tag"
                                    aria-label={`Tag ${t}`}
                                >
                                    #{t}
                                </Link>
                            ))}
                        </span>
                    )}
                </div>
                {coverUrl && (
                    <figure className="msn-hero-wrap">
                        <img
                            className="msn-hero"
                            src={coverUrl}
                            alt={`${m.missionName} cover`}
                            loading="lazy"
                        />
                    </figure>
                )}
            </header>

            {/* GRID */}
            {/* Grid: main + aside */}
            <div className="msn-grid">
                <main className="msn-main">
                    {/* Objectives */}
                    {hasObjectives && (
                        <section className="msn-section">
                            <h2 className="msn-h2">Objectives</h2>
                            <div className="msn-objwrap">
                                {m.objectives?.primary?.length > 0 && (
                                    <div className="msn-card subtle">
                                        <h3 className="msn-h3">Primary</h3>
                                        <ul className="msn-list msn-progress-list">
                                            {m.objectives.primary.map((obj, i) => {
                                                const done = primaryChecklist.doneSet.has(i);
                                                return (
                                                    <li key={i} className={done ? "done" : "pending"}>
                                                        {obj}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                                {m.objectives?.optional?.length > 0 && (
                                    <div className="msn-card subtle">
                                        <h3 className="msn-h3">Optional</h3>
                                        <ul className="msn-list msn-progress-list">
                                            {m.objectives.optional.map((obj, i) => {
                                                const done = optionalChecklist.doneSet.has(i);
                                                return (
                                                    <li key={i} className={done ? "done" : "pending"}>
                                                        {obj}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Walkthrough */}
                    {m.walkthrough?.trim?.() && (
                        <section className="msn-section">
                            <h2 className="msn-h2">Walkthrough</h2>
                            <div className="msn-md msn-prose">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {m.walkthrough}
                                </ReactMarkdown>
                            </div>
                        </section>
                    )}

                    {/* Approaches */}
                    {m.approaches?.trim?.() && (
                        <section className="msn-section">
                            <h2 className="msn-h2">Different Approaches</h2>
                            <div className="msn-md msn-prose">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {m.approaches}
                                </ReactMarkdown>
                            </div>
                        </section>
                    )}

                    {/* Tips */}
                    {m.tips?.trim?.() && (
                        <section className="msn-section">
                            <h2 className="msn-h2">Tips & Tricks</h2>
                            <div className="msn-md msn-prose">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {m.tips}
                                </ReactMarkdown>
                            </div>
                        </section>
                    )}

                    {/* Story */}
                    {m.story?.trim?.() && (
                        <section className="msn-section">
                            <h2 className="msn-h2">Story</h2>
                            <div className="msn-md msn-prose">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {m.story}
                                </ReactMarkdown>
                            </div>
                        </section>
                    )}

                    {/* Gallery */}
                    {Array.isArray(galleryUrls) && galleryUrls.length > 0 && (
                        <section className="msn-section">
                            <h2 className="msn-h2">Gallery</h2>
                            <div className="msn-gallery">
                                {galleryUrls.map((src, i) => (
                                    <button
                                        key={i}
                                        className="msn-shotbtn"
                                        onClick={() => openLightbox(i)}
                                        aria-label={`Open screenshot ${i + 1}`}
                                    >
                                        <img
                                            className="msn-shot"
                                            src={src}
                                            alt={`Screenshot ${i + 1}`}
                                            loading="lazy"
                                        />
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Video (embed if possible) */}
                    {m.videoUrl && (
                        <section className="msn-section">
                            <h2 className="msn-h2">Video</h2>
                            {yt ? (
                                <div className="msn-video">
                                    <iframe
                                        title="Mission video"
                                        src={yt}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        loading="lazy"
                                    />
                                </div>
                            ) : (
                                <a
                                    className="msn-link"
                                    href={m.videoUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    ▶ Open video
                                </a>
                            )}
                        </section>
                    )}
                </main>

                {/* Sidebar */}
                <aside className="msn-aside" aria-label="Quick facts">
                    <div className="msn-card">
                        <div className="msn-card-title">Quick facts</div>
                        <div className="msn-facts">
                            {m.difficulty && (
                                <div>
                                    <span>Difficulty</span>
                                    <strong>{m.difficulty}</strong>
                                </div>
                            )}
                            {m.estDurationMins && (
                                <div>
                                    <span>Est. time</span>
                                    <strong>{m.estDurationMins} mins</strong>
                                </div>
                            )}
                            {m.location && (
                                <div>
                                    <span>Location</span>
                                    <strong>{m.location}</strong>
                                </div>
                            )}
                        </div>
                    </div>

                    {(m.rewards?.cash != null || m.rewards?.items) && (
                        <div className="msn-card">
                            <div className="msn-card-title">Rewards</div>
                            <div className="msn-rewards">
                                {m.rewards?.cash != null && (
                                    <div className="msn-rewardchip">
                                        💵 {m.rewards.cash.toLocaleString?.() ?? m.rewards.cash}
                                    </div>
                                )}
                                {m.rewards?.items && (
                                    <div className="msn-rewardchip">🎒 {m.rewards.items}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Progress card */}
                    {uid ? (
                        <div className="msn-card">
                            <div className="msn-card-title">Your Progress</div>

                            {/* Head summary */}
                            <div className="msn-progress-head">
                                <div className="msn-progress-count">
                                    <strong>{doneAll}</strong>/<span>{totalObjectives}</span>{" "}
                                    objectives
                                </div>
                                <div className="msn-progress-pills">
                                    {primaryCount > 0 && (
                                        <span className="msn-pill">
                                            Primary {donePrimary}/{primaryCount}
                                        </span>
                                    )}
                                    {optionalCount > 0 && (
                                        <span className="msn-pill">
                                            Optional {doneOptional}/{optionalCount}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Percent bar */}
                            <div
                                className="msn-progressbar"
                                aria-label={`Progress ${pct}%`}
                            >
                                <div
                                    className="msn-progressbar-fill"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <div className="msn-progressbar-meta">{pct}% complete</div>

                            {/* Collapsible checklists (easier to skim) */}
                            <div className="msn-progress">
                                {/* Primary objectives list */}
                                {m.objectives?.primary?.length > 0 && (
                                    <details className="msn-accordion" open>
                                        <summary>Primary objectives</summary>
                                        <ul className="msn-list msn-progress-list">
                                            {m.objectives.primary.map((obj, i) => {
                                                const done = primaryChecklist.doneSet.has(i);
                                                return (
                                                    <li key={i} className={done ? "done" : "pending"}>
                                                        {obj}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </details>
                                )}

                                {/* Optional objectives list */}
                                {m.objectives?.optional?.length > 0 && (
                                    <details className="msn-accordion">
                                        <summary>Optional objectives</summary>
                                        <ul className="msn-list msn-progress-list">
                                            {m.objectives.optional.map((obj, i) => {
                                                const done = optionalChecklist.doneSet.has(i);
                                                return (
                                                    <li key={i} className={done ? "done" : "pending"}>
                                                        {obj}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </details>
                                )}

                                {completion?.completed && (
                                    <p className="msn-complete-msg">🎉 Mission completed!</p>
                                )}
                                {!completion && (
                                    <p className="msn-progress-hint">
                                        Start completing objectives to track your progress.
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="msn-card">
                            <div className="msn-card-title">Your Progress</div>
                            <p className="msn-progress-hint">
                                Sign in to track your progress.
                            </p>
                        </div>
                    )}
                </aside>
            </div>
            {/* Engagement row (INSIDE PANEL) */}
            <div className="msn-engage msn-engage--panel">
                <PresenceBadge presenceKey={presenceKey} entityId={missionId} />

                <LikeBar basePath={basePath} />

                <ReactionBar
                    basePath={basePath}
                    visibleReactions={[
                        "love",
                        "fire",
                        "helpful",
                        "strategy",
                        "insightful",
                    ]}
                    highlight={featuredReaction}
                    onSelect={onReactionSelect}
                />

                <Bookmark bookmarkKey={bookmarkKey} />

                <MissionCompleted basePath={basePath} showCount mission={m} />
            </div>

            {/* Discussion (INSIDE PANEL) */}
            <div className="msn-discussion-panel">
                <Discussion basePath={basePath} authorId={m.createdBy} />
            </div>

        </>
    );
}
